import { createSelector } from 'reselect';
// Import enumerations.
import {
	GpsAccuracy,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	AddressLocationWithAccuracy,
	BuildingLimitsPersonal,
	BuildingLimitsPersonalWithRcv,
	BuildingResponsePersonalWithStatus,
	BuildingRiskDataPersonal,
	GpsCoordinatesWithAccuracy,
	NewBusinessApplication,
} from '@app/models/api';
// Import selectors.
import {
	makeGetNewBusinessApplicationSelector,
} from '@app/redux/selectors';

// Get Building Personal Selector.
const makeGetBuildingPersonalSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): BuildingResponsePersonalWithStatus | undefined => {
		return newBusinessApplication !== undefined
			&& newBusinessApplication.risk !== null
			? newBusinessApplication.risk
			: undefined;
	}
);

// Get Building Risk Data Personal Selector.
const makeGetBuildingRiskDataPersonalSelector = () => createSelector(
	[makeGetBuildingPersonalSelector()],
	(buildingPersonal: BuildingResponsePersonalWithStatus | undefined): BuildingRiskDataPersonal | undefined => {
		return buildingPersonal !== undefined
			&& buildingPersonal.buildingData !== null
			? buildingPersonal.buildingData
			: undefined;
	}
);

// Get Building Replacement Cost Value Personal Selector.
const makeGetBuildingReplacementCostValuePersonalSelector = () => createSelector(
	[makeGetBuildingRiskDataPersonalSelector()],
	(buildingRiskDataPersonal: BuildingRiskDataPersonal | undefined): number | undefined => {
		return buildingRiskDataPersonal !== undefined
			&& buildingRiskDataPersonal.buildingReplacementCostValue !== null
			? buildingRiskDataPersonal.buildingReplacementCostValue
			: undefined;
	}
);

// Get Building Limits Personal Selector.
const makeGetBuildingLimitsPersonalSelector = () => createSelector(
	[makeGetBuildingPersonalSelector()],
	(buildingPersonal: BuildingResponsePersonalWithStatus | undefined): BuildingLimitsPersonal | undefined => {
		return buildingPersonal !== undefined
			&& buildingPersonal.limits !== null
			? buildingPersonal.limits
			: undefined;
	}
);

// Get Building Limits Personal With Rcv Selector.
const makeGetBuildingLimitsPersonalWithRcvSelector = () => createSelector(
	[makeGetBuildingLimitsPersonalSelector(), makeGetBuildingReplacementCostValuePersonalSelector()],
	(buildingLimitsPersonal: BuildingLimitsPersonal | undefined, buildingReplacementCostValue: number | undefined): BuildingLimitsPersonalWithRcv => {
		let buildingLimitsPersonalWithRcv: BuildingLimitsPersonalWithRcv = new BuildingLimitsPersonalWithRcv();
		buildingLimitsPersonalWithRcv.building = buildingLimitsPersonal?.building ?? null;
		buildingLimitsPersonalWithRcv.buildingReplacementCostValue = buildingReplacementCostValue ?? null;
		buildingLimitsPersonalWithRcv.contents = buildingLimitsPersonal?.contents ?? null;
		buildingLimitsPersonalWithRcv.otherStructures = buildingLimitsPersonal?.otherStructures ?? null;
		buildingLimitsPersonalWithRcv.additionalLivingExpense = buildingLimitsPersonal?.additionalLivingExpense ?? null;
		buildingLimitsPersonalWithRcv.contentsUseReplacementCostValue = buildingLimitsPersonal?.contentsUseReplacementCostValue ?? null;

		return buildingLimitsPersonalWithRcv;
	}
);

// Get Property Address Location With Accuracy Selector.
const makeGetPropertyAddressLocationWithAccuracySelector = () => createSelector(
	[makeGetBuildingPersonalSelector()],
	(buildingPersonal: BuildingResponsePersonalWithStatus | undefined): AddressLocationWithAccuracy | undefined => {
		return buildingPersonal !== undefined
			&& buildingPersonal.address !== null
			? buildingPersonal.address
			: undefined;
	}
);

// Get Property Location Selector.
const makeGetPropertyLocationSelector = () => createSelector(
	[makeGetPropertyAddressLocationWithAccuracySelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): GpsCoordinatesWithAccuracy | undefined => {
		return addressLocationWithAccuracy !== undefined
			&& addressLocationWithAccuracy.gpsCoordinates !== null
			? addressLocationWithAccuracy.gpsCoordinates
			: undefined;
	}
);

// Get Property Address Selector.
const makeGetPropertyAddressSelector = () => createSelector(
	[makeGetPropertyAddressLocationWithAccuracySelector()],
	(addressLocationWithAccuracy: AddressLocationWithAccuracy | undefined): Address | undefined => {
		return addressLocationWithAccuracy !== undefined
			&& addressLocationWithAccuracy.address !== null
			? addressLocationWithAccuracy.address
			: undefined;
	}
);

// Get Property Address Gps Accuracy Selector.
const makeGetPropertyAddressGpsAccuracy = () => createSelector(
	[makeGetPropertyLocationSelector()],
	(propertyLocation: GpsCoordinatesWithAccuracy | undefined): GpsAccuracy | undefined => {
		return propertyLocation !== undefined
			&& propertyLocation.gpsAccuracy !== null
			? propertyLocation.gpsAccuracy
			: undefined;
	}
);


// Export composed methods.
export {
	
};

// export selectors.
export {
	makeGetBuildingPersonalSelector,
	makeGetBuildingRiskDataPersonalSelector,
	makeGetBuildingReplacementCostValuePersonalSelector,
	makeGetBuildingLimitsPersonalSelector,
	makeGetBuildingLimitsPersonalWithRcvSelector,
	makeGetPropertyAddressLocationWithAccuracySelector,
	makeGetPropertyLocationSelector,
	makeGetPropertyAddressSelector,
	makeGetPropertyAddressGpsAccuracy,
};